import React, { useState } from 'react';
import { DocumentTextIcon, ArrowDownTrayIcon, BuildingOffice2Icon, CalendarIcon, CurrencyDollarIcon, ExclamationCircleIcon, CheckCircleIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import type { XmlTerceiro } from '../../types/xmlTerceiros';
import { useBaixarDanfe, useBaixarXml } from '../../hooks/useXmlTerceiros';
import { formatDateTime } from '../../utils/format';
import ParcelasDialog from './ParcelasDialog';

interface XmlTerceirosListProps {
  xmlTerceiros: XmlTerceiro[];
  onSelect: (xml: XmlTerceiro) => void;
  isLoading: boolean;
}

const XmlTerceirosList: React.FC<XmlTerceirosListProps> = ({
  xmlTerceiros,
  onSelect,
  isLoading
}) => {
  const baixarDanfeMutation = useBaixarDanfe();
  const baixarXmlMutation = useBaixarXml();
  const [parcelasNfe, setParcelasNfe] = useState<XmlTerceiro | null>(null);

  const handleOpenParcelas = (e: React.MouseEvent, xml: XmlTerceiro) => {
    e.stopPropagation();
    setParcelasNfe(xml);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };


  const formatCnpj = (cnpj: number) => {
    const cnpjStr = cnpj.toString().padStart(14, '0');
    return cnpjStr.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const handleBaixarDanfe = (e: React.MouseEvent, nfeId: number) => {
    e.stopPropagation();
    baixarDanfeMutation.mutate(nfeId);
  };

  const handleBaixarXml = (e: React.MouseEvent, nfeId: number) => {
    e.stopPropagation();
    baixarXmlMutation.mutate(nfeId);
  };

  if (isLoading) {
    return (
      <div className="bg-surface rounded-iris-lg shadow-sm border border-line">
        <div className="p-8 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-accent border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-tx-2">Carregando XML de terceiros...</p>
        </div>
      </div>
    );
  }

  if (!xmlTerceiros || xmlTerceiros.length === 0) {
    return (
      <div className="bg-surface rounded-iris-lg shadow-sm border border-line">
        <div className="p-8 text-center">
          <DocumentTextIcon className="h-12 w-12 text-tx-3 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-tx mb-2">Nenhum XML encontrado</h3>
          <p className="text-tx-2">Não há XML de terceiros com os filtros aplicados.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-iris-lg shadow-sm border border-line overflow-hidden">
      {/* Header para desktop */}
      <div className="hidden lg:block bg-surface-2 px-6 py-3 border-b border-line">
        <div className="grid grid-cols-12 gap-4 text-sm font-medium text-tx-3">
          <div className="col-span-1">NFe</div>
          <div className="col-span-3">Emitente</div>
          <div className="col-span-2">CNPJ</div>
          <div className="col-span-2">Data/Hora</div>
          <div className="col-span-2">Valor</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1">Ações</div>
        </div>
      </div>

      {/* Lista de XML */}
      <div className="divide-y divide-line">
        {xmlTerceiros.map((xml) => (
          <div
            key={xml.gid}
            onClick={() => onSelect(xml)}
            className="p-4 lg:p-6 hover:bg-surface-2 cursor-pointer transition-colors"
          >
            {/* Layout Mobile */}
            <div className="lg:hidden space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-tx">
                      NFe {xml.numero}
                    </span>
                    {xml.pendente ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning-soft text-warning text-xs font-medium rounded-full">
                        <ExclamationCircleIcon className="h-3 w-3" />
                        Pendente
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-ok-soft text-ok text-xs font-medium rounded-full">
                        <CheckCircleIcon className="h-3 w-3" />
                        Processado
                      </span>
                    )}
                  </div>

                  <div className="mt-1 space-y-1">
                    <div className="flex items-center gap-2 text-sm text-tx-2">
                      <BuildingOffice2Icon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">
                        {xml.emitenteNome || xml.emitenteFantasia || 'Nome não informado'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-tx-2">
                      <DocumentTextIcon className="h-4 w-4 flex-shrink-0" />
                      <span>{formatCnpj(xml.emitenteCnpj)}</span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-tx-2">
                      <CalendarIcon className="h-4 w-4 flex-shrink-0" />
                      <span>{formatDateTime(xml.dataHoraEmissao)}</span>
                    </div>

                    <div className="flex items-center gap-2 text-sm font-medium text-tx">
                      <CurrencyDollarIcon className="h-4 w-4 flex-shrink-0" />
                      <span>{formatCurrency(xml.valorTotalNf)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ações Mobile */}
              <div className="flex gap-2 pt-2 border-t border-line">
                <button
                  onClick={(e) => handleOpenParcelas(e, xml)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-accent-text bg-accent-soft rounded-iris hover:bg-accent hover:text-white transition-colors"
                  title="Ver parcelamento"
                >
                  <CreditCardIcon className="h-4 w-4" />
                  Parcelas
                </button>
                <button
                  onClick={(e) => handleBaixarDanfe(e, xml.gid)}
                  disabled={baixarDanfeMutation.isPending}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-accent-text bg-accent-soft rounded-iris hover:opacity-80 transition-opacity disabled:opacity-50"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  PDF
                </button>
                <button
                  onClick={(e) => handleBaixarXml(e, xml.gid)}
                  disabled={baixarXmlMutation.isPending}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-ok bg-ok-soft rounded-iris hover:opacity-80 transition-opacity disabled:opacity-50"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  XML
                </button>
              </div>
            </div>

            {/* Layout Desktop */}
            <div className="hidden lg:grid grid-cols-12 gap-4 items-center">
              <div className="col-span-1">
                <span className="font-semibold text-tx">{xml.numero}</span>
              </div>

              <div className="col-span-3">
                <div>
                  <p className="font-medium text-tx truncate">
                    {xml.emitenteNome || 'Nome não informado'}
                  </p>
                  {xml.emitenteFantasia && xml.emitenteFantasia !== xml.emitenteNome && (
                    <p className="text-sm text-tx-2 truncate">
                      {xml.emitenteFantasia}
                    </p>
                  )}
                </div>
              </div>

              <div className="col-span-2">
                <span className="text-sm text-tx-2">{formatCnpj(xml.emitenteCnpj)}</span>
              </div>

              <div className="col-span-2">
                <span className="text-sm text-tx-2">{formatDateTime(xml.dataHoraEmissao)}</span>
              </div>

              <div className="col-span-2">
                <span className="font-medium text-tx">{formatCurrency(xml.valorTotalNf)}</span>
              </div>

              <div className="col-span-1">
                {xml.pendente ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning-soft text-warning text-xs font-medium rounded-full">
                    <ExclamationCircleIcon className="h-3 w-3" />
                    Pendente
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-ok-soft text-ok text-xs font-medium rounded-full">
                    <CheckCircleIcon className="h-3 w-3" />
                    Processado
                  </span>
                )}
              </div>

              <div className="col-span-1">
                <div className="flex gap-1">
                  <button
                    onClick={(e) => handleOpenParcelas(e, xml)}
                    className="p-1 text-accent-text hover:bg-accent-soft rounded-iris-sm transition-colors"
                    title="Ver parcelamento"
                  >
                    <CreditCardIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => handleBaixarDanfe(e, xml.gid)}
                    disabled={baixarDanfeMutation.isPending}
                    className="p-1 text-accent-text hover:bg-accent-soft rounded transition-colors disabled:opacity-50"
                    title="Baixar DANFE (PDF)"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => handleBaixarXml(e, xml.gid)}
                    disabled={baixarXmlMutation.isPending}
                    className="p-1 text-ok hover:bg-ok-soft rounded transition-colors disabled:opacity-50"
                    title="Baixar XML"
                  >
                    <DocumentTextIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ParcelasDialog
        nfeId={parcelasNfe?.gid ?? null}
        numeroNfe={parcelasNfe?.numero}
        isOpen={!!parcelasNfe}
        onClose={() => setParcelasNfe(null)}
      />
    </div>
  );
};

export default XmlTerceirosList;